<script lang="ts" setup>
import { object, string } from 'yup';

import blogLogo from '~/assets/icons/logo/blog.svg?url';

const { t, locale } = useI18n();
const route = useValidatedRoute({ params: object().shape({ slug: string().required() }) });
const getImage = useImage();
const applyApprovedFilter = useArticleApprovedFilter();

const [{ data: page }, { data: recommendedSentences }, { data: popularSentences }] = await Promise.all([
  useAsyncData(route.path, () => queryCollection('blog').path(`/${route.params.slug}`).first()),

  useAsyncData(`${route.path}_recommended`, () =>
    applyApprovedFilter(
      queryCollection('blog')
        .where('section_recommended', '=', 1)
        .where('category', '=', 'articles')
        .where('path', '<>', `/${route.params.slug}`),
    )
      .limit(4)
      .select('path', 'title', 'image', 'category', 'downloadUrl')
      .all(),
  ),

  useAsyncData(`${route.path}_popular`, () =>
    applyApprovedFilter(
      queryCollection('blog')
        .where('section_popular', '=', 1)
        .where('category', '=', 'articles')
        .where('path', '<>', `/${route.params.slug}`),
    )
      .limit(3)
      .select('path', 'title', 'image', 'category', 'downloadUrl')
      .all(),
  ),
]);

assertNotEmpty(page, () => createError({ statusCode: 404, statusMessage: 'Page Not Found' }));

useSeoMeta({
  title: page.value.title,
  ogTitle: page.value.title,
  description: page.value.description,
  ogDescription: page.value.description,
});

const currentCategory = computed(() => BLOG_CATEGORIES.find((el) => el.code === page.value.category));

const coverStyle = computed(
  () =>
    `url(${getImage(page.value.cover, {
      format: 'webp',
    })}) center / cover no-repeat`,
);
const dateString = computed(() => {
  const dateString: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'long',
  };
  return page.value.date && new Date(page.value.date).toLocaleDateString(locale.value, dateString);
});

useSchemaOrg([
  defineArticle({
    image: page.value.cover,
    datePublished: page.value.date,
    dateModified: page.value.date,
  }),
]);
</script>

<template>
  <main class="articlePage">
    <div class="articlePage__header">
      <NuxtLink to="/blog" class="articlePage__logo" :class="{ articlePage__logo_extra: page.extraTitle }">
        <img :src="blogLogo" :alt="t('logo_alt')" />
      </NuxtLink>
      <h1 v-if="page.extraTitle" class="articlePage__extraTitle">
        {{ page.extraTitle }}
      </h1>
      <span v-if="currentCategory" class="articlePage__label" :style="{ backgroundColor: currentCategory.color }">
        {{ t(`blog.category.${page.category}`) }}
      </span>
      <h1 v-if="!page.extraTitle" class="articlePage__title">
        {{ page.title }}
      </h1>
    </div>

    <div class="articlePage__container">
      <div class="articlePage__content">
        <BaseBreadcrumb :items="[{ label: t('blog'), to: '/blog' }]" class="articlePage__breadcrumb" />
        <span v-if="page.author" class="articlePage__additional">
          {{ page.author }}
        </span>
        <span v-if="page.date" class="articlePage__additional">
          {{ dateString }}
        </span>
        <BaseArticle as-child>
          <template #default="props">
            <ContentRenderer :value="page" v-bind="props" />
          </template>
        </BaseArticle>

        <SchemaFaqContent v-if="page.body" :data="page.body" />
      </div>

      <div class="articlePage__popular" v-if="popularSentences?.length">
        <h3 class="articlePage__popularTitle">{{ t('popular') }}</h3>
        <div class="articlePage__popularList">
          <BlogCard v-for="item in popularSentences" :key="item.path" :article="item" horizontal />
        </div>
      </div>
    </div>

    <div class="articlePage__sentences" v-if="recommendedSentences?.length">
      <h3 class="articlePage__sentencesTitle">{{ t('recommended') }}</h3>
      <div class="articlePage__sentencesList">
        <BlogCard v-for="item in recommendedSentences" :key="item.path" :article="item" />
      </div>
    </div>
  </main>
</template>

<i18n src="~/locales/blog.yaml"></i18n>
<i18n>
ru:
  logo_alt: Логотип блога
  popular: Популярное
  recommended: Рекомендуем также
  blog: Статьи

en:
  logo_alt: Blog logo
  popular: Popular
  recommended: Recommended
  blog: Blog
</i18n>

<style lang="scss">
@font-face {
  font-family: 'Monosten Stencil CY';
  src:
    local('Monosten Stencil CY'),
    url('@/assets/fonts/MonostenStencilCY-Regular.woff2') format('woff2'),
    url('@/assets/fonts/MonostenStencilCY-Regular.ttf') format('truetype');
  font-weight: 500;
  font-style: normal;
  font-display: swap;
}

.articlePage {
  &__header {
    height: 591px;
    background: v-bind(coverStyle);
    padding: 36px 137px;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    position: relative;

    @include mq('sm') {
      height: 417px;
      padding: 17px 20px 29px;
    }
  }

  &__logo {
    filter: invert(1);
    position: absolute;
    top: 20px;
    left: 20px;
    width: 188px;
    height: 182px;

    @include mq('sm') {
      width: 121px;
      height: 126px;
    }

    &_extra {
      @include mq('sm') {
        display: none;
      }
    }
  }

  &__label {
    width: 148px;
    height: 32px;
    border-radius: 30px;
    font-size: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #ffffff;
  }

  &__extraTitle {
    font-family: 'Monosten Stencil CY', sans-serif;
    margin-top: 0;
    margin-bottom: 24px;
    white-space: pre-wrap;
    font-size: 64px;
    line-height: 0.9;
    font-weight: 500;
    max-width: 300px;
    text-transform: uppercase;

    @include mq('md') {
      font-size: 56px;
    }

    @include mq('sm') {
      font-size: 48px;
      margin-bottom: 16px;
    }

    @include mq('xs') {
      font-size: 42px;
    }
  }

  &__title {
    font-size: 56px;
    line-height: 64px;
    color: #ffffff;
    margin-top: 24px;
    margin-bottom: 0;
    max-width: 823px;

    @include mq('lg') {
      font-size: 44px;
      line-height: 120%;
    }

    @include mq('sm') {
      margin-top: 16px;
      font-size: 24px;
      line-height: 32px;
      max-width: initial;
    }
  }

  &__container {
    display: flex;
    padding: 0 20px;

    @include mq('sm') {
      padding: 0 16px;
    }
  }

  &__content {
    padding-top: 32px;
    padding-bottom: 40px;
    margin-left: calc(100% / 12);
    margin-right: 20px;
    width: 800px;
    max-width: 100%;
    overflow: hidden;

    @include mq('md') {
      margin: 0 auto;
    }
  }

  &__breadcrumb {
    margin-bottom: 12px;
  }

  &__additional {
    margin-bottom: 16px;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.2px;
    color: rgba(0, 0, 0, 0.4);
    display: block;
  }

  &__popular {
    padding-top: 30px;
    padding-bottom: 20px;
    width: calc(100% / 3);
    margin-left: auto;

    @include mq('lg') {
      min-width: 240px;
    }

    @include mq('md') {
      display: none;
    }
  }

  &__popularTitle {
    font-size: 24px;
    line-height: 32px;
    letter-spacing: 0.2px;
    color: #121212;
    margin-bottom: 22px;
  }

  &__popularList {
    display: flex;
    flex-direction: column;
    gap: 16px;

    // TODO: надо бы сделать лучше
    .blogCard__wrapper {
      width: 177px;

      @include mq('lg') {
        width: 100%;
      }
    }
  }

  &__sentences {
    padding: 36px 20px 80px;

    @include mq('sm') {
      padding: 0 16px 60px;
    }
  }

  &__sentencesTitle {
    font-size: 34px;
    line-height: 40px;
    letter-spacing: 0.2px;
    color: #121212;
    margin-top: 0;
    margin-bottom: 32px;

    @include mq('sm') {
      margin-bottom: 16px;
      font-size: 24px;
      line-height: 32px;
    }
  }

  &__sentencesList {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;

    @include mq('sm') {
      display: flex;
      flex-direction: column;
    }
  }

  &__subscribe {
    @include mq('sm') {
      display: none;
    }
  }
}
</style>
